import { useMutation, useQueryClient } from '@tanstack/react-query';

import { MutationFunction, useRequest } from '@frontend/api';

import type { SavedView, SavedViewPayload } from './types';
import { getSavedViewsListQueryKey } from './utils';

type CreateSavedViewContext = { previousViews: SavedView[] | undefined };

export const useCreateSavedView: MutationFunction<
  SavedView,
  SavedViewPayload
> = ({ query, requestOptions, queryOptions } = {}) => {
  const request = useRequest(requestOptions);
  const queryClient = useQueryClient();
  const listKey = getSavedViewsListQueryKey();

  return useMutation<
    SavedView,
    unknown,
    SavedViewPayload,
    CreateSavedViewContext
  >({
    mutationFn: (requestData) =>
      request<SavedView>(`/display_configs/views`, {
        query,
        init: {
          method: 'POST',
          body: requestData,
        },
      }),
    ...queryOptions,
    onMutate: async (vars) => {
      queryOptions?.onMutate?.(vars);

      await queryClient.cancelQueries({ queryKey: listKey });
      const previousViews = queryClient.getQueryData<SavedView[]>(listKey);
      queryClient.setQueryData(listKey, (old?: SavedView[]) =>
        [
          ...(old ?? []),
          {
            id: (old?.length ?? 1) * -1,
            userOwner: {},
            ...vars,
          } as SavedView,
        ].sort((a, b) => a.name.localeCompare(b.name)),
      );

      return { previousViews };
    },
    onError: (...args) => {
      queryOptions?.onError?.(...args);

      const [, , context] = args;
      queryClient.setQueryData(listKey, context?.previousViews ?? []);
    },
    onSettled: (...args) => {
      queryOptions?.onSettled?.(...args);

      queryClient.invalidateQueries({ queryKey: listKey });
    },
    mutationKey: ['createSavedView'],
  });
};
